import React, { useEffect, useMemo } from "react";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";

interface ReadCycleFilters {
  meterType: string;
  status: string;
}

interface ReadCycleFiltersProps {
  meterType: string;
  status: string;
  meterTypeList: Array<{ label: string; value: string | number }>;
  statusList: Array<{ label: string; value: string }>;
  onApplyFilters: (filters: ReadCycleFilters) => void;
  onResetFilters: () => void;
}

export const ReadCycleFiltersPanel: React.FC<ReadCycleFiltersProps> = ({
  meterType,
  status,
  meterTypeList,
  statusList,
  onApplyFilters,
  onResetFilters,
}) => {
  // Transform lists to options format
  const meterTypeOptions = useMemo(
    () =>
      meterTypeList?.map((type) => ({
        value: type.label,
        label: type.label,
      })) || [],
    [meterTypeList]
  );

  const statusOptions = useMemo(
    () =>
      statusList?.map((status) => ({
        value: status.value,
        label: status.label,
      })) || [],
    [statusList]
  );

  // Define filter fields
  const filterFields: FormField[] = useMemo(
    () => [
      {
        name: "meterType",
        label: "Read Type",
        type: "select",
        placeholder: "Select Read Type",
        options: meterTypeOptions,
        className: "col-span-1",
        classes: {
          container: "w-full mb-6",
        },
      },
      {
        name: "status",
        label: "Status",
        type: "select",
        placeholder: "Select Status",
        options: statusOptions,
        className: "col-span-1",
        classes: {
          container: "w-full mb-6",
        },
      },
    ],
    [meterTypeOptions, statusOptions]
  );

  // Create form instance
  const form = FormService.createForm({
    fields: filterFields,
    mode: "onChange",
    validateOnMount: false,
  });

  // Sync form values when URL params change
  useEffect(() => {
    form.reset({
      meterType: decodeURIComponent(meterType) || "",
      status: decodeURIComponent(status) || "",
    });
  }, [meterType, status, form]);

  const handleFilterSubmit = () => {
    const formValues = form.getValues();
    onApplyFilters({
      meterType: formValues.meterType || "",
      status: formValues.status || "",
    });
  };

  const handleFilterReset = () => {
    form.reset({
      meterType: "",
      status: "",
    });
    onResetFilters();
  };

  const handleFieldChange = (field: string, value: string) => {
    // Add any field-specific logic here if needed
  };

  return (
    <div>
      <DynamicForm
        fields={filterFields}
        form={form}
        onSubmit={handleFilterSubmit}
        onReset={handleFilterReset}
        onFieldChange={handleFieldChange}
        gridColumns={3}
        config={{
          layout: "vertical",
          showSubmitButton: true,
          showResetButton: true,
          submitButtonText: "Apply Filters",
          resetButtonText: "Clear",
          classes: {
            buttonContainer: "flex justify-end gap-4",
          },
        }}
      />
    </div>
  );
};
